import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LearningResourceRequestStatusEnum } from 'src/app/enums/learning-resource-request-status.enum';
import { LearningResourceRequestStatusEnumLabels } from 'src/app/enums/learning-resource-request-status.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { AcceptLearningResourceRequestBody, LearningResourceRequest } from 'src/app/models/learning_resource_request.model';
import { Skill } from 'src/app/models/skill.model';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-learning-resource-request-review-dialog',
  templateUrl: './learning-resource-request-review-dialog.component.html',
  styleUrl: './learning-resource-request-review-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class LearningResourceRequestReviewDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<LearningResourceRequestReviewDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);

  readonly data = inject<LearningResourceRequest>(MAT_DIALOG_DATA);

  readonly learning_resource_request_status_enum_labels = LearningResourceRequestStatusEnumLabels;

  readonly loading = signal(false);
  readonly skills = signal<Skill[]>([]);
  readonly is_pending = this.data.status === LearningResourceRequestStatusEnum.Pending;

  readonly proposer_full_name = this.data.user ? `${this.data.user.name} ${this.data.user.surnames}`.trim() : '-';

  readonly accept_form = this.form_builder.group({
    name: [this.data.title, [Validators.required, Validators.maxLength(255)]],
    expiration_in_months: [12, [Validators.required, Validators.min(1)]],
    duration_in_hours: [null as number | null],
    skill_ids: [[] as number[]],
  });

  ngOnInit(): void {
    this.initSkills();
  }

  decline(): void {
    if (this.loading()) {
      return;
    }

    this.loading.set(true);

    this.api_service
      .declineLearningResourceRequest(this.data.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => this.dialog_ref.close(true),
        error: (err: { error?: { message?: string }; message?: string }): void => {
          this.loading.set(false);
          const message = err?.error?.message ?? err?.message ?? 'Error al rechazar la solicitud.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  accept(): void {
    if (this.loading() || this.accept_form.invalid) {
      return;
    }

    this.loading.set(true);

    const form_value = this.accept_form.getRawValue();
    const body: AcceptLearningResourceRequestBody = {
      name: form_value.name!,
      expiration_in_months: form_value.expiration_in_months!,
      duration_in_hours: form_value.duration_in_hours,
      skill_ids: form_value.skill_ids ?? [],
    };

    this.api_service
      .acceptLearningResourceRequest(this.data.id, body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => this.dialog_ref.close(true),
        error: (err: { error?: { message?: string }; message?: string }): void => {
          this.loading.set(false);
          const message = err?.error?.message ?? err?.message ?? 'Error al aceptar la solicitud.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  close(): void {
    if (!this.loading()) {
      this.dialog_ref.close(false);
    }
  }

  private initSkills(): void {
    this.api_service
      .getSkills({ per_page: 200, order_by: 'name:asc' })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<Skill>): void => {
          this.skills.set(response.data);
        },
      });
  }
}
